import type { DbExecutor } from "./executor";
import { generateId } from "./ids";
import {
  alreadyPresent,
  activeWalls,
  archiveWall,
  createWall,
  hostWallInPlan,
  nodeOf,
  openingFitsWall,
  placeOpening,
  planUpstreamSync,
  setOpeningProps,
  archiveOpening,
  type EntityChange,
  type OpContext,
  type PlanModel,
  type UpstreamAction,
} from "../geometry";
import { PlanRepository } from "./repositories/planRepository";

/**
 * CARRYING A DISCOVERY FORWARD (spec §60H).
 *
 * The user notices the dining room always had a window, adds it to Existing,
 * and expects their plans to know. This service is what makes that true.
 *
 * For every Proposed design in the project it asks the pure planner what one
 * Existing edit means, then:
 *
 *   • applies the safe ones — a plan that had no opinion about that window
 *     simply gets it, through the ordinary geometry operations, so the
 *     result is indistinguishable from having drawn it there;
 *   • records the rest as something to look at, WITHOUT touching the plan.
 *     A plan that moved that window, or removed it, keeps its decision.
 *
 * Nothing here invents geometry: if the wall an opening belongs on is not
 * recognisably the same wall in that plan, or the opening would not fit, it
 * becomes a review item instead of a guess.
 */

export interface UpstreamChangeRow {
  designId: string;
  changeKey: string;
  entityKind: string;
  changeKind: string;
  rootId: string;
  summary: string;
  reason: string;
  status: "pending" | "applied" | "kept";
  detectedAt: string;
}

export interface UpstreamSyncOutcome {
  /** Designs that silently took the change, and how many entities each got. */
  applied: { designId: string; count: number }[];
  /** Designs with something for the user to look at. */
  review: { designId: string; count: number }[];
}

export class DesignSyncService {
  private readonly plans: PlanRepository;

  constructor(private readonly db: DbExecutor) {
    this.plans = new PlanRepository(db);
  }

  private ctx(): OpContext {
    return { newId: () => generateId(), now: () => new Date().toISOString() };
  }

  /**
   * One Existing edit, offered to every proposed design of the project.
   * Called by the coordinator after a commit on the canonical Existing
   * design; a commit on any other design changes nothing here.
   */
  async propagateFromExisting(
    projectId: string,
    existingDesignId: string,
    floorId: string,
    changes: readonly EntityChange[],
  ): Promise<UpstreamSyncOutcome> {
    const outcome: UpstreamSyncOutcome = { applied: [], review: [] };
    if (changes.length === 0) return outcome;

    const proposedDesigns = await this.db.select<{ id: string }>(
      `SELECT id FROM designs WHERE project_id = ? AND kind = 'proposed' AND archived_at IS NULL AND id <> ?
        ORDER BY sort_order, created_at`,
      [projectId, existingDesignId],
    );

    for (const design of proposedDesigns) {
      const model = await this.plans.loadFloor(design.id, floorId);
      const actions = planUpstreamSync(changes, model);
      if (actions.length === 0) continue;

      let applied = 0;
      let review = 0;
      let current = model;
      const pending: EntityChange[] = [];

      for (const action of actions) {
        if (action.disposition === "apply") {
          const result = this.apply(current, action);
          if (result) {
            current = result.model;
            pending.push(...result.changes);
            applied += 1;
            await this.record(design.id, action, "applied");
            continue;
          }
          // It could not be applied honestly — say so rather than force it.
          await this.record(
            design.id,
            { ...action, reason: "It could not be placed in this plan automatically." },
            "pending",
          );
          review += 1;
          continue;
        }
        await this.record(design.id, action, "pending");
        review += 1;
      }

      if (pending.length > 0) {
        await this.plans.applyChanges(design.id, pending, "forward");
      }
      if (applied > 0) outcome.applied.push({ designId: design.id, count: applied });
      if (review > 0) outcome.review.push({ designId: design.id, count: review });
    }

    return outcome;
  }

  /** Apply one safe action to a plan, or return null if it cannot be done honestly. */
  private apply(model: PlanModel, action: UpstreamAction): { model: PlanModel; changes: EntityChange[] } | null {
    const ctx = this.ctx();
    try {
      if (action.entityKind === "opening" && action.changeKind === "added" && action.opening) {
        if (alreadyPresent(model, action)) return null;
        const hostRoot = action.hostRootWallId;
        const wallId = hostRoot ? hostWallInPlan(model, hostRoot) : null;
        if (!wallId) return null;
        if (!openingFitsWall(model, wallId, action.opening)) return null;
        const result = placeOpening(model, ctx, wallId, {
          kind: action.opening.kind,
          offsetFromStart: action.opening.offsetFromStart,
          width: action.opening.width,
          height: action.opening.height,
          sillHeight: action.opening.sillHeight,
          swing: action.opening.swing,
          windowConfig: action.opening.windowConfig,
          windowOperation: action.opening.windowOperation,
          notes: action.opening.notes,
        });
        // Lineage: the plan's copy points at the same root as Existing's, so
        // the diff engine and every later sync recognise them as one thing.
        const created = result.model.openings.get(result.openingId)!;
        const linked = { ...created, originId: action.rootId };
        const model2: PlanModel = { ...result.model, openings: new Map(result.model.openings) };
        (model2.openings as Map<string, typeof linked>).set(linked.id, linked);
        const changes: EntityChange[] = [
          ...result.changes.filter((c) => !(c.kind === "opening" && c.id === linked.id)),
          { kind: "opening", id: linked.id, before: null, after: linked },
        ];
        return { model: model2, changes };
      }

      if (action.entityKind === "opening" && action.changeKind === "changed" && action.opening && action.targetId) {
        const result = setOpeningProps(model, ctx, action.targetId, {
          kind: action.opening.kind,
          width: action.opening.width,
          height: action.opening.height,
          sillHeight: action.opening.sillHeight,
          swing: action.opening.swing,
          windowConfig: action.opening.windowConfig,
          windowOperation: action.opening.windowOperation,
        });
        return { model: result.model, changes: result.changes };
      }

      if (action.entityKind === "opening" && action.changeKind === "removed" && action.targetId) {
        const result = archiveOpening(model, ctx, action.targetId);
        return { model: result.model, changes: result.changes };
      }

      // A WALL is never applied on its own initiative — it reaches here only
      // because the user answered "use the Existing version" (spec §60H).
      if (action.entityKind === "wall" && action.changeKind === "added" && action.wall) {
        if (alreadyPresent(model, action)) return null;
        const result = createWall(model, ctx, { point: action.wall.start }, { point: action.wall.end }, {
          kind: action.wall.kind,
          thickness: action.wall.thickness,
          heightInches: action.wall.heightInches,
          exteriorOverride: action.wall.exteriorOverride,
          notes: action.wall.notes,
        });
        // Landing across this plan's own geometry would split it into several
        // walls, and one of them being "the same wall" would be a guess. The
        // user is asked to draw it where they want it instead.
        if (result.wallIds.length !== 1) return null;
        const created = result.model.walls.get(result.wallIds[0])!;
        const linked = { ...created, originId: action.rootId };
        const walls = new Map(result.model.walls);
        walls.set(linked.id, linked);
        const model2: PlanModel = { ...result.model, walls };
        const changes: EntityChange[] = [
          ...result.changes.filter((c) => !(c.kind === "wall" && c.id === linked.id)),
          { kind: "wall", id: linked.id, before: null, after: linked },
        ];
        return { model: model2, changes };
      }

      if (action.entityKind === "wall" && action.changeKind === "removed" && action.targetId) {
        const result = archiveWall(model, ctx, action.targetId);
        return { model: result.model, changes: result.changes };
      }
    } catch {
      // A geometry refusal is an answer: it becomes a review item.
      return null;
    }
    return null;
  }

  private async record(designId: string, action: UpstreamAction, status: "pending" | "applied"): Promise<void> {
    const now = new Date().toISOString();
    const existing = await this.db.select<{ status: string }>(
      "SELECT status FROM design_upstream_changes WHERE design_id = ? AND change_key = ?",
      [designId, action.key],
    );
    if (existing.length > 0) {
      // A decision the user already made is never reopened by a later pass.
      if (existing[0].status !== "pending") return;
      await this.db.execute(
        `UPDATE design_upstream_changes SET change_kind = ?, summary = ?, reason = ?, status = ?, detected_at = ?,
           resolved_at = CASE WHEN ? = 'pending' THEN NULL ELSE ? END
         WHERE design_id = ? AND change_key = ?`,
        [action.changeKind, action.summary, action.reason ?? "", status, now, status, now, designId, action.key],
      );
      return;
    }
    await this.db.execute(
      `INSERT INTO design_upstream_changes
         (design_id, change_key, entity_kind, change_kind, root_id, summary, reason, status, detected_at, resolved_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        designId,
        action.key,
        action.entityKind,
        action.changeKind,
        action.rootId,
        action.summary,
        action.reason ?? "",
        status,
        now,
        status === "pending" ? null : now,
      ],
    );
  }

  /** What this plan still has to look at. */
  async pendingFor(designId: string): Promise<UpstreamChangeRow[]> {
    const rows = await this.db.select<{
      design_id: string;
      change_key: string;
      entity_kind: string;
      change_kind: string;
      root_id: string;
      summary: string;
      reason: string;
      status: string;
      detected_at: string;
    }>(
      `SELECT design_id, change_key, entity_kind, change_kind, root_id, summary, reason, status, detected_at
         FROM design_upstream_changes WHERE design_id = ? AND status = 'pending'
        ORDER BY detected_at, change_key`,
      [designId],
    );
    return rows.map((r) => ({
      designId: r.design_id,
      changeKey: r.change_key,
      entityKind: r.entity_kind,
      changeKind: r.change_kind,
      rootId: r.root_id,
      summary: r.summary,
      reason: r.reason,
      status: r.status as UpstreamChangeRow["status"],
      detectedAt: r.detected_at,
    }));
  }

  /** "Keep this plan's version" — the difference stops being a question. */
  async keep(designId: string, changeKey: string): Promise<void> {
    await this.db.execute(
      "UPDATE design_upstream_changes SET status = 'kept', resolved_at = ? WHERE design_id = ? AND change_key = ?",
      [new Date().toISOString(), designId, changeKey],
    );
  }

  /**
   * "Take the change into this plan" — the user's explicit decision, applied
   * through the same operations the automatic path uses.
   */
  async applyPending(designId: string, floorId: string, changeKey: string, existingDesignId: string): Promise<boolean> {
    const pending = (await this.pendingFor(designId)).find((row) => row.changeKey === changeKey);
    if (!pending) return false;

    const existingModel = await this.plans.loadFloor(existingDesignId, floorId);
    const proposedModel = await this.plans.loadFloor(designId, floorId);

    // Rebuild the action from the current Existing state, so applying late
    // takes what Existing says NOW, not a stale copy.
    const action = this.actionFromCurrentExisting(existingModel, proposedModel, pending);
    if (!action) return false;
    const result = this.apply(proposedModel, action);
    if (!result) return false;
    await this.plans.applyChanges(designId, result.changes, "forward");
    await this.db.execute(
      "UPDATE design_upstream_changes SET status = 'applied', resolved_at = ? WHERE design_id = ? AND change_key = ?",
      [new Date().toISOString(), designId, changeKey],
    );
    return true;
  }

  private actionFromCurrentExisting(
    existing: PlanModel,
    proposed: PlanModel,
    row: UpstreamChangeRow,
  ): UpstreamAction | null {
    if (row.entityKind === "wall") return this.wallActionFromCurrentExisting(existing, proposed, row);
    if (row.entityKind !== "opening") return null;
    const source = [...existing.openings.values()].find((o) => (o.originId ?? o.id) === row.rootId);
    if (!source) return null;
    const hostWall = existing.walls.get(source.wallId);
    if (!hostWall) return null;
    const target = [...proposed.openings.values()].find((o) => (o.originId ?? o.id) === row.rootId);
    if (source.archivedAt !== null) {
      return target && target.archivedAt === null
        ? {
            key: row.changeKey,
            entityKind: "opening",
            changeKind: "removed",
            rootId: row.rootId,
            disposition: "apply",
            summary: row.summary,
            reason: null,
            targetId: target.id,
          }
        : null;
    }
    if (target && target.archivedAt === null) {
      return {
        key: row.changeKey,
        entityKind: "opening",
        changeKind: "changed",
        rootId: row.rootId,
        disposition: "apply",
        summary: row.summary,
        reason: null,
        opening: source,
        targetId: target.id,
      };
    }
    return {
      key: row.changeKey,
      entityKind: "opening",
      changeKind: "added",
      rootId: row.rootId,
      disposition: "apply",
      summary: row.summary,
      reason: null,
      opening: source,
      hostRootWallId: hostWall.originId ?? hostWall.id,
    };
  }

  /**
   * The wall behind a review item, as Existing has it NOW: taking it late
   * takes what the existing house currently says, never a stale copy.
   */
  private wallActionFromCurrentExisting(
    existing: PlanModel,
    proposed: PlanModel,
    row: UpstreamChangeRow,
  ): UpstreamAction | null {
    const source = [...existing.walls.values()].find((w) => (w.originId ?? w.id) === row.rootId);
    const inPlan = [...proposed.walls.values()].find((w) => (w.originId ?? w.id) === row.rootId && w.archivedAt === null);
    if (!source) return null;
    if (source.archivedAt !== null) {
      return inPlan
        ? {
            key: row.changeKey,
            entityKind: "wall",
            changeKind: "removed",
            rootId: row.rootId,
            disposition: "review",
            summary: row.summary,
            reason: null,
            targetId: inPlan.id,
          }
        : null;
    }
    if (inPlan) return null;
    const start = nodeOf(existing, source.startNodeId);
    const end = nodeOf(existing, source.endNodeId);
    return {
      key: row.changeKey,
      entityKind: "wall",
      changeKind: "added",
      rootId: row.rootId,
      disposition: "review",
      summary: row.summary,
      reason: null,
      wall: {
        start: { x: start.x, y: start.y },
        end: { x: end.x, y: end.y },
        kind: source.kind,
        thickness: source.thickness,
        heightInches: source.heightInches,
        exteriorOverride: source.exteriorOverride,
        notes: source.notes,
      },
    };
  }

  /** Every plan's pending count, for the design switcher's badge. */
  async pendingCounts(projectId: string): Promise<Map<string, number>> {
    const rows = await this.db.select<{ design_id: string; n: number }>(
      `SELECT u.design_id, COUNT(*) AS n FROM design_upstream_changes u
         JOIN designs d ON d.id = u.design_id
        WHERE d.project_id = ? AND u.status = 'pending'
        GROUP BY u.design_id`,
      [projectId],
    );
    return new Map(rows.map((r) => [r.design_id, Number(r.n)]));
  }
}

/** Walls of a plan, exposed for tests that assert nothing was touched. */
export function planWallCount(model: PlanModel): number {
  return activeWalls(model).length;
}
